/**
 * A source's results, grouped by pipeline step: conversion, indexing, and the quality
 * assessment made at indexing (rag_documents.source_quality). Only what exists is shown.
 */
import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { Meta } from './styles';

const Groups = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const Group = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 12px;
  color: #ccc;
  dl { margin: 6px 0 0; display: grid; grid-template-columns: auto 1fr; gap: 4px 10px; }
  dt { color: #888; }
  dd { margin: 0; word-break: break-word; }
`;

const GroupTitle = styled.strong`
  font-size: 12px;
  color: #fff;
`;

const Synopsis = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #bbb;
`;

function Rows({ rows }) {
  const shown = rows.filter(([, v]) => v != null && v !== '');
  if (!shown.length) return null;
  return (
    <dl>
      {shown.map(([k, v]) => (
        <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>
      ))}
    </dl>
  );
}

const score = (v) => (typeof v === 'number' ? v.toFixed(2) : null);
const date = (v) => (v ? new Date(v).toLocaleString() : null);

export default function SourceResults({ source }) {
  const { t } = useTranslation();
  const r = (key, vars) => t(`datasetWorkspace.results.${key}`, vars);
  const convert = source.metadata?.convertMeta || {};
  const match = source.metadata?.match;
  const q = source.source_quality || {};

  const matchText = match?.own
    ? t('datasetImport.own')
    : match?.auto
      ? `${t('datasetImport.auto')} · ${Math.round((match.score || 0) * 100)} % · ${t(`datasetImport.methods.${match.method}`)}`
      : match ? t('datasetImport.manual') : null;

  const hasIndex = source.document_id != null;
  const hasQuality = Object.keys(q).length > 0;

  return (
    <>
      <Groups>
        <Group>
          <GroupTitle>{r('conversion')}</GroupTitle>
          <Rows rows={[
            [r('file'), source.filename],
            [r('pages'), convert.totalPages != null
              ? t('datasetWorkspace.pages', { pages: convert.totalPages, visual: convert.visualPages?.length ?? 0 })
              : null],
            [r('images'), convert.vision
              ? `${r('imagesRead', { described: convert.vision.described, pages: convert.vision.pages })}${convert.vision.error ? ` · ${convert.vision.error}` : ''}`
              : convert.visualPages?.length ? r('imagesNotRead') : null],
            [r('matched'), matchText],
            [r('cost'), source.cost_estimate_usd != null ? `${source.cost_estimate_usd} $` : null],
            [r('reviewReason'), source.review_reason],
            [r('rejectionReason'), source.rejection_reason],
          ]} />
        </Group>
        {hasIndex && (
          <Group>
            <GroupTitle>{r('index')}</GroupTitle>
            <Rows rows={[
              [r('chunks'), source.chunk_count],
              [r('words'), source.word_count],
              [r('language'), source.doc_language],
              [r('topics'), Array.isArray(source.topics) && source.topics.length ? source.topics.join(', ') : null],
              [r('indexedAt'), date(source.indexed_at)],
            ]} />
          </Group>
        )}
        {hasQuality && (
          <Group>
            <GroupTitle>{r('quality')}</GroupTitle>
            <Rows rows={[
              [r('credibility'), score(q.credibility_score)],
              [r('researchBasis'), score(q.research_basis_score)],
              [r('recency'), score(q.recency_score)],
              [r('documentType'), q.document_type],
              [r('publisherType'), q.publisher_type],
              [r('year'), q.publication_year],
              [r('bias'), q.bias_direction],
            ]} />
          </Group>
        )}
      </Groups>
      {source.synopsis && <Synopsis>{source.synopsis}</Synopsis>}
      {!hasIndex && !convert.totalPages && !source.filename && <Meta>{r('none')}</Meta>}
    </>
  );
}
